// src/services/api.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

// Configuration
const config = {
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second initial delay
  retryStatusCodes: [408, 429, 500, 502, 503, 504],
};

// Extend axios config to track retries
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: config.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Utility: Calculate exponential backoff delay
const getRetryDelay = (retryCount: number): number => {
  return config.retryDelay * Math.pow(2, retryCount);
};

// Utility: Check if error is retryable
const isRetryableError = (error: AxiosError): boolean => {
  // Network errors are retryable
  if (!error.response) {
    return error.code !== 'ECONNABORTED' || error.message?.includes('timeout');
  }

  // Check status code
  return config.retryStatusCodes.includes(error.response.status);
};

// Utility: Sleep for a given duration
const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void; config: any }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.config.headers.Authorization = `Bearer ${token}`;
      prom.resolve(api(prom.config));
    }
  });
  failedQueue = [];
};

// Fonction pour refresh token sans dépendance circulaire
const refreshToken = async (): Promise<string> => {
  const response = await api.post<{ access_token: string; token_type: string }>('/auth/refresh');
  const { access_token } = response.data;
  localStorage.setItem('access_token', access_token);
  return access_token;
};

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with retry logic
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Check if this is an auth endpoint - don't try to refresh tokens for these
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
                          originalRequest?.url?.includes('/auth/register') ||
                          originalRequest?.url?.includes('/auth/send-otp') ||
                          originalRequest?.url?.includes('/auth/verify-otp') ||
                          originalRequest?.url?.includes('/auth/forgot-password') ||
                          originalRequest?.url?.includes('/auth/verify-reset-code') ||
                          originalRequest?.url?.includes('/auth/reset-password');

    // Retry logic for transient errors
    if (isRetryableError(error) && !isAuthEndpoint) {
      const retryCount = originalRequest._retryCount || 0;

      if (retryCount < config.retryAttempts) {
        originalRequest._retryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount);

        console.log(`[API] Retrying request (${retryCount + 1}/${config.retryAttempts}) after ${delay}ms:`, originalRequest.url);

        await sleep(delay);
        return api(originalRequest);
      }
    }

    // Token refresh logic for 401 errors
    if (error.response && error.response.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ config: originalRequest, resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        refreshToken()
          .then((token) => {
            processQueue(null, token);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err);
            localStorage.removeItem('access_token');
            // Use a custom event instead of hard redirect for better SPA handling
            window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'token_expired' } }));
            // Fallback redirect if event not handled
            setTimeout(() => {
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            }, 100);
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // Handle other 401s (not auth endpoints)
    if (error.response && error.response.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      localStorage.removeItem('access_token');
      window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'unauthorized' } }));
    }

    // Enhance error object with additional info
    const enhancedError = error as AxiosError & {
      isNetworkError?: boolean;
      isTimeoutError?: boolean;
      isServerError?: boolean;
    };

    enhancedError.isNetworkError = !error.response && error.code !== 'ECONNABORTED';
    enhancedError.isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
    enhancedError.isServerError = error.response?.status ? error.response.status >= 500 : false;

    return Promise.reject(enhancedError);
  }
);

export default api;
